import _ from 'lodash';
import { Response } from 'miragejs';
import { valueFromASTUntyped } from 'graphql';
import alertRulesFixture from './data/platform-ncl/alert-rules.json';
import eventRulesFixture from './data/platform-ncl/alert-event-rules-list.json';

const ALERT_RULES_ENDPOINT = /^\/organizations\/[^/]+\/alert\/rules$/;
const BUILT_IN_MONITORS_FILTER = /\bdriftAlert eq false\b/;
const PROBLEM_MEDIA_TYPE = 'application/vnd.netapp.bxp.problem+json';
// The alerts service treats eq on these fields as a case-insensitive contains search.
const TEXT_SEARCH_FIELDS = ['name', 'description'];
const SORT_FIELD_PATHS = {
    effectiveMetricValue: 'threshold.effectiveMetricValue'
};

let alertRules = [];

const tokenizeFilter = (filter) => {
    const tokens = [];
    let index = 0;
    while (index < filter.length) {
        const char = filter[index];
        if (/\s/.test(char)) {
            index += 1;
        } else if (char === '(' || char === ')') {
            tokens.push({ type: char });
            index += 1;
        } else if (char === "'") {
            let value = '';
            index += 1;
            while (index < filter.length && !(filter[index] === "'" && filter[index + 1] !== "'")) {
                value += filter[index];
                index += filter[index] === "'" ? 2 : 1;
            }
            if (index >= filter.length) {
                throw new Error(`Unterminated string in filter: ${filter}`);
            }
            tokens.push({ type: 'literal', value });
            index += 1;
        } else {
            const [word] = filter.slice(index).match(/^[^\s()']+/);
            tokens.push({ type: 'word', value: word });
            index += word.length;
        }
    }
    return tokens;
};

const parseLiteral = (word) => {
    if (word === 'true' || word === 'false') {
        return word === 'true';
    }
    if (word === 'null') {
        return null;
    }
    const number = Number(word);
    return Number.isFinite(number) ? number : word;
};

// "or" binds tighter than "and": the alerts service reads "a and b or c" as "a and (b or c)",
// which is how the UI joins the values of one multi-select column.
const parseFilter = (filter) => {
    const tokens = tokenizeFilter(filter);
    let position = 0;

    function fail() {
        throw new Error(`Unsupported filter: ${filter}`);
    }

    function isKeyword(keyword) {
        return tokens[position]?.type === 'word' && tokens[position].value.toLowerCase() === keyword;
    }

    function parseComparison() {
        const [field, operator, operand] = tokens.slice(position, position + 3);
        const operatorName = operator?.type === 'word' ? operator.value.toLowerCase() : null;
        if (field?.type !== 'word' || !['eq', 'ne'].includes(operatorName) || !['word', 'literal'].includes(operand?.type)) {
            fail();
        }
        position += 3;
        return {
            field: field.value,
            operator: operatorName,
            value: operand.type === 'literal' ? operand.value : parseLiteral(operand.value)
        };
    }

    function parseOperand() {
        if (tokens[position]?.type !== '(') {
            return parseComparison();
        }
        position += 1;
        const node = parseAnd();
        if (tokens[position]?.type !== ')') {
            fail();
        }
        position += 1;
        return node;
    }

    function parseOr() {
        const nodes = [parseOperand()];
        while (isKeyword('or')) {
            position += 1;
            nodes.push(parseOperand());
        }
        return nodes.length > 1 ? { or: nodes } : nodes[0];
    }

    function parseAnd() {
        const nodes = [parseOr()];
        while (isKeyword('and')) {
            position += 1;
            nodes.push(parseOr());
        }
        return nodes.length > 1 ? { and: nodes } : nodes[0];
    }

    const tree = parseAnd();
    if (position < tokens.length) {
        fail();
    }
    return tree;
};

const matchesComparison = (item, { field, operator, value }) => {
    const itemValue = _.get(item, field);
    const isMatch = TEXT_SEARCH_FIELDS.includes(field) && _.isString(value)
        ? _.toLower(itemValue).includes(_.toLower(value))
        : _.isEqual(itemValue ?? null, value);
    return operator === 'ne' ? !isMatch : isMatch;
};

const matchesFilter = (item, node) => {
    if (node.and) {
        return node.and.every((child) => matchesFilter(item, child));
    }
    if (node.or) {
        return node.or.some((child) => matchesFilter(item, child));
    }
    return matchesComparison(item, node);
};

const sortItems = (items, orderBy) => {
    const [field, direction] = _.trim(orderBy).split(/\s+/);
    if (!field) {
        return items;
    }
    const path = SORT_FIELD_PATHS[field] ?? field;
    const order = _.toLower(direction) === 'desc' ? -1 : 1;
    return [...items].sort((left, right) => {
        const leftValue = _.get(left, path);
        const rightValue = _.get(right, path);
        if (_.isNil(leftValue) || _.isNil(rightValue)) {
            return Number(_.isNil(leftValue)) - Number(_.isNil(rightValue));
        }
        if (leftValue === rightValue) {
            return 0;
        }
        return leftValue < rightValue ? -order : order;
    });
};

const toNonNegativeInteger = (value) => {
    const number = _.isNil(value) || value === '' ? NaN : Number(value);
    return Number.isInteger(number) && number >= 0 ? number : null;
};

const pageItems = (items, { limit, skip, page }) => {
    const pageSize = toNonNegativeInteger(limit);
    if (pageSize === null) {
        return items;
    }
    const offset = toNonNegativeInteger(skip) ?? Math.max((toNonNegativeInteger(page) ?? 1) - 1, 0) * pageSize;
    return items.slice(offset, offset + pageSize);
};

const queryRules = (rules, params = {}) => {
    const filterTree = params.filter ? parseFilter(params.filter) : null;
    const matches = filterTree ? rules.filter((rule) => matchesFilter(rule, filterTree)) : rules;
    return {
        count: matches.length,
        rules: _.cloneDeep(pageItems(sortItems(matches, params.orderBy), params))
    };
};

const isCustomizedRule = ({ isEnabled, defaultEnabled, threshold, sustainedFor }) => (
    isEnabled !== defaultEnabled
    || !_.isEqual(threshold?.effectiveMetricValue, threshold?.defaultMetricValue)
    || !_.isEqual(sustainedFor?.effectiveInMinutes, sustainedFor?.defaultInMinutes)
);

// A null effective value in the PATCH body restores the default.
const applyRuleUpdate = (rule, { isEnabled, threshold, sustainedFor }) => {
    if (_.isBoolean(isEnabled)) {
        rule.isEnabled = isEnabled;
    }
    if (threshold && rule.threshold) {
        rule.threshold.effectiveMetricValue = threshold.effectiveMetricValue ?? rule.threshold.defaultMetricValue;
    }
    if (sustainedFor && rule.sustainedFor) {
        rule.sustainedFor.effectiveInMinutes = sustainedFor.effectiveInMinutes ?? rule.sustainedFor.defaultInMinutes;
    }
    rule.isCustomized = isCustomizedRule(rule);
};

const readRequestBody = (request) => {
    const body = request?.requestBody;
    return typeof body === 'string' ? JSON.parse(body) : (body ?? {});
};

/**
 * Answers the Monitors page alert rule and event rule requests for the current simulator session.
 * Storage class (drift) monitor lists are not handled here and keep their config-ncl.json filters.
 * @param {string} key - The config-ncl.json seeder key of the matched route.
 * @param {Object} request - The Mirage request.
 * @returns {Object|Response|undefined} The response, or undefined when the seeder should answer from config.
 */
const handleAlertRulesRequest = (key, request) => {
    const params = request?.queryParams || {};
    try {
        if (key === 'alert-rules-list' && (!params.filter || BUILT_IN_MONITORS_FILTER.test(params.filter))) {
            return queryRules(alertRules, params);
        }
        if (key === 'alert-rules-update') {
            _.forEach(readRequestBody(request).items, (item) => {
                const rule = alertRules.find(({ id }) => id === item?.id);
                if (rule) {
                    applyRuleUpdate(rule, item);
                }
            });
            return new Response(202);
        }
        if (key === 'alert-event_rules') {
            return queryRules(eventRulesFixture.rules, params);
        }
    } catch (error) {
        return new Response(400, { 'Content-Type': PROBLEM_MEDIA_TYPE }, {
            type: 'https://bluexp.netapp.io/problems/1',
            title: 'Bad Request',
            status: '400',
            detail: error.message
        });
    }
    return undefined;
};

const isAlertRulesTarget = ({ serviceIdentifier, method, endpoint }) => (
    serviceIdentifier === 'ALERTS' && _.toUpper(method) === 'GET' && ALERT_RULES_ENDPOINT.test(endpoint)
);

const aggregateTarget = (target, staticResult) => {
    const { identifier, params } = target;
    if (!isAlertRulesTarget(target)) {
        return staticResult ?? { identifier, statusCode: 404, success: false, data: null };
    }
    try {
        return { identifier, statusCode: 200, success: true, data: queryRules(alertRules, params) };
    } catch (_error) {
        return { identifier, statusCode: 400, success: false, data: null };
    }
};

/**
 * Answers an aggregateApis GraphQL query by running each alert rules target against the session's monitors,
 * so the Monitors page cards always agree with its tables. Every other target keeps its entry from the
 * static "aggregate" GraphQL stub, matched by identifier.
 * @param {Object} parsedQuery - The parsed GraphQL document.
 * @param {Object} [variables] - The GraphQL request variables.
 * @param {Object} [staticResponse] - The configured "aggregate" GraphQL stub, if any.
 * @returns {Object|undefined}
 */
const handleAggregateApisRequest = (parsedQuery, variables, staticResponse) => {
    const aggregateField = parsedQuery?.definitions?.[0]?.selectionSet?.selections
        ?.find((selection) => selection.name?.value === 'aggregateApis');
    const targetsArgument = aggregateField?.arguments?.find((argument) => argument.name.value === 'targets');
    if (!targetsArgument) {
        return staticResponse;
    }
    const targets = valueFromASTUntyped(targetsArgument.value, variables);
    const staticResults = _.keyBy(staticResponse?.data?.aggregateApis, 'identifier');
    return {
        data: { aggregateApis: _.map(targets, (target) => aggregateTarget(target, staticResults[target.identifier])) }
    };
};

const resetAlertRulesSimulator = () => {
    alertRules = _.cloneDeep(alertRulesFixture.rules);
};

resetAlertRulesSimulator();

export {
    handleAlertRulesRequest,
    handleAggregateApisRequest,
    resetAlertRulesSimulator
};
